/**
 * Service for real-time agent job status updates via Server-Sent Events
 *
 * Provides:
 * - SSE subscription for real-time job status updates
 * - REST API calls for executing agent jobs
 * - Callbacks for progress, completion, and error events
 */

import { getAuthToken } from './firebase';
import { logger } from '../utils/logger';
import { getApiBaseUrl } from '../utils/apiConfig';
import api from './api';

// Agent job status types
export interface AgentJobProgress {
  percentage: number;
  currentOperation: string;
  thinking?: string;
}

export interface AgentJobStatusEvent {
  type: 'connected' | 'status' | 'complete' | 'heartbeat' | 'error';
  jobId: string;
  status?: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  progress?: AgentJobProgress;
  result?: Record<string, any>;
  error?: string;
  timestamp: string;
  connectionId?: string;
  message?: string;
}

// Callbacks for job subscription
export interface AgentJobCallbacks {
  onProgress?: (progress: AgentJobProgress) => void;
  onComplete?: (result: Record<string, any>) => void;
  onError?: (error: string) => void;
  onStatusChange?: (status: string) => void;
}

// Input for executing an agent job
export interface ExecuteAgentParams {
  conversationId: string;
  agentName: string;
  inputs: Record<string, any>;
  metadata?: Record<string, any>;
  priority?: number;
  delay?: number;
}

// Response from execute agent endpoint
export interface ExecuteAgentResponse {
  success: boolean;
  job: {
    id: string;
    agentName: string;
    status: string;
    createdAt: string;
  };
  error?: string;
}

/**
 * Subscribe to agent job status updates via SSE
 *
 * @param jobId - The job ID to subscribe to
 * @param callbacks - Object with callback functions for various events
 * @returns Unsubscribe function to close the connection
 */
export function subscribeToAgentJob(
  jobId: string,
  callbacks: AgentJobCallbacks
): () => void {
  let eventSource: EventSource | null = null;
  let isActive = true;

  // Async IIFE to get fresh token before connecting
  (async () => {
    const baseUrl = getApiBaseUrl();
    const token = await getAuthToken();
    const url = token
      ? `${baseUrl}/agent-jobs/${jobId}/stream?token=${encodeURIComponent(token)}`
      : `${baseUrl}/agent-jobs/${jobId}/stream`;

    if (!isActive) return;

    logger.info(`[AgentJobService] Subscribing to job ${jobId} status updates`);

    eventSource = new EventSource(url);

    eventSource.onopen = () => {
      logger.info(`[AgentJobService] SSE connection opened for job ${jobId}`);
    };

    eventSource.onmessage = (event) => {
      if (!isActive) return;

      try {
        const data: AgentJobStatusEvent = JSON.parse(event.data);
        handleEvent(data, callbacks, jobId);
      } catch (error) {
        logger.error(`[AgentJobService] Error parsing event for job ${jobId}:`, error);
      }
    };

    eventSource.onerror = (error) => {
      logger.error(`[AgentJobService] SSE error for job ${jobId}:`, error);

      if (eventSource?.readyState === EventSource.CLOSED) {
        logger.warn(`[AgentJobService] SSE connection closed for job ${jobId}`);
      }
    };
  })();

  // Return unsubscribe function
  return () => {
    isActive = false;
    eventSource?.close();
    logger.info(`[AgentJobService] Unsubscribed from job ${jobId}`);
  };
}

// Track completed jobs to prevent duplicate onComplete calls
const completedJobs = new Set<string>();

/**
 * Handle incoming SSE events
 */
function handleEvent(
  data: AgentJobStatusEvent,
  callbacks: AgentJobCallbacks,
  jobId: string
): void {
  logger.debug(`[AgentJobService] Received event for job ${jobId}:`, data);

  switch (data.type) {
    case 'connected':
      logger.info(`[AgentJobService] Connected to job ${jobId} stream:`, data.connectionId);
      break;

    case 'status':
      // Status update with progress
      if (data.status) {
        callbacks.onStatusChange?.(data.status);
      }
      // Only dispatch progress for active (non-terminal) statuses.
      // Dispatching progress after COMPLETED/FAILED would re-add state that
      // onStatusChange just cleaned up, causing UI flicker.
      if (data.progress && data.status !== 'COMPLETED' && data.status !== 'FAILED') {
        callbacks.onProgress?.(data.progress);
      }
      // Check for error in result (terminate_reason: ERROR case - agent stuck in reasoning loops)
      // This is a fallback in case the worker sends COMPLETED instead of FAILED
      if (data.status === 'COMPLETED' && data.result?.terminate_reason === 'ERROR') {
        if (!completedJobs.has(jobId)) {
          completedJobs.add(jobId);
          const errorMsg = data.result.result || 'Agent terminated with error';
          logger.error(`[AgentJobService] Job ${jobId} completed with ERROR terminate_reason:`, errorMsg);
          callbacks.onError?.(errorMsg);
        }
        return; // Don't call onComplete
      }
      // If this status update indicates completion/failure (call onComplete only once)
      if (data.status === 'COMPLETED' && data.result && !completedJobs.has(jobId)) {
        completedJobs.add(jobId);
        callbacks.onComplete?.(data.result);
      } else if (data.status === 'FAILED' && data.error && !completedJobs.has(jobId)) {
        completedJobs.add(jobId);
        callbacks.onError?.(data.error);
      }
      break;

    case 'complete':
      // Stream ended - final status (call onComplete only if not already called)
      logger.info(`[AgentJobService] Job ${jobId} completed with status: ${data.status}`);
      // Check for error in result (terminate_reason: ERROR case)
      if (data.status === 'COMPLETED' && data.result?.terminate_reason === 'ERROR') {
        if (!completedJobs.has(jobId)) {
          completedJobs.add(jobId);
          const errorMsg = data.result.result || 'Agent terminated with error';
          logger.error(`[AgentJobService] Job ${jobId} complete event with ERROR terminate_reason:`, errorMsg);
          callbacks.onError?.(errorMsg);
        }
      } else if (data.status === 'COMPLETED' && !completedJobs.has(jobId)) {
        completedJobs.add(jobId);
        callbacks.onComplete?.(data.result || {});
      } else if (data.status === 'FAILED' && !completedJobs.has(jobId)) {
        completedJobs.add(jobId);
        callbacks.onError?.(data.error || 'Job failed');
      }
      // Clean up the completed job from tracking after a delay
      // Use 60s to prevent race conditions with hooks that poll/reconnect
      setTimeout(() => completedJobs.delete(jobId), 60000);
      break;

    case 'heartbeat':
      logger.debug(`[AgentJobService] Heartbeat received for job ${jobId}`);
      break;

    case 'error':
      logger.error(`[AgentJobService] Error from server for job ${jobId}:`, data.message);
      callbacks.onError?.(data.message || 'Unknown error');
      break;

    default:
      logger.debug(`[AgentJobService] Unknown event type for job ${jobId}:`, data.type);
  }
}

/**
 * Execute an agent job via REST API
 *
 * @param params - Parameters for executing the agent
 * @returns Promise with the job ID and initial status
 */
export async function executeAgentJob(
  params: ExecuteAgentParams
): Promise<ExecuteAgentResponse> {
  logger.info(`[AgentJobService] Executing agent: ${params.agentName}`, {
    conversationId: params.conversationId,
    inputKeys: Object.keys(params.inputs),
  });

  const response = await api.post<ExecuteAgentResponse>('/agent-jobs/execute', {
    conversationId: params.conversationId,
    agentName: params.agentName,
    inputs: params.inputs,
    metadata: params.metadata,
    priority: params.priority,
    delay: params.delay,
  });

  if (!response.data.success) {
    throw new Error(response.data.error || 'Failed to execute agent');
  }

  logger.info(`[AgentJobService] Agent job created:`, {
    jobId: response.data.job.id,
    agentName: response.data.job.agentName,
    status: response.data.job.status,
  });

  return response.data;
}

/**
 * Get status of an agent job via REST API (for fallback/polling)
 */
export async function getAgentJobStatus(jobId: string): Promise<{
  success: boolean;
  job: {
    id: string;
    agentName: string;
    status: string;
    progress: AgentJobProgress;
    result?: Record<string, any>;
    error?: string;
    createdAt: string;
    completedAt?: string;
  };
}> {
  const response = await api.get(`/agent-jobs/${jobId}`);
  return response.data;
}

/**
 * Cancel an agent job
 */
export async function cancelAgentJob(jobId: string): Promise<{
  success: boolean;
  message?: string;
  error?: string;
}> {
  const response = await api.post(`/agent-jobs/${jobId}/cancel`);
  return response.data;
}

/**
 * Get active agent jobs for a conversation
 * Useful for reconnecting to ongoing jobs after page refresh or slide switch
 */
export async function getActiveAgentJobs(conversationId: string): Promise<{
  success: boolean;
  jobs: Array<{
    id: string;
    agentName: string;
    status: string;
    progress: AgentJobProgress;
    createdAt: string;
    startedAt?: string;
    metadata?: Record<string, any>;
  }>;
}> {
  const response = await api.get(`/conversations/${conversationId}/agent-jobs/active`);
  return response.data;
}

/**
 * Convenience function to execute agent and subscribe to updates
 * Returns the job ID and an unsubscribe function
 */
export async function executeAndSubscribe(
  params: ExecuteAgentParams,
  callbacks: AgentJobCallbacks
): Promise<{ jobId: string; unsubscribe: () => void }> {
  // Execute the agent job
  const response = await executeAgentJob(params);
  const jobId = response.job.id;

  // Subscribe to status updates
  const unsubscribe = subscribeToAgentJob(jobId, callbacks);

  return { jobId, unsubscribe };
}

export default {
  subscribeToAgentJob,
  executeAgentJob,
  getAgentJobStatus,
  cancelAgentJob,
  getActiveAgentJobs,
  executeAndSubscribe,
};
